import React, { useEffect, useState } from 'react';
import { Button, Input, Select, Tag, Typography, notification } from 'antd';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { CustomProduct, CustomProductInput, refetchCustomProduct, updateCustomProduct } from '../../store/customProductSlice';
import { KeyValueListEditor } from './Editors/KeyValueListEditor';
import {
  getLanguageOptionsForMarketplace,
  getPreferredMarketplaceForLanguage,
  isSupportedKeywordResearchLocale,
  keywordResearchLanguages,
  keywordResearchMarketplaces,
  normalizeLocaleCode,
} from '../../constants/keywordResearchLocales';

interface Props {
  product: CustomProduct;
}

type ProductInputField = keyof CustomProductInput;

interface TextFieldConfig {
  key: ProductInputField;
  label: string;
  multiline?: boolean;
}

interface ListFieldConfig {
  key: ProductInputField;
  label: string;
  tone?: 'global' | 'high' | 'optional' | 'negative';
}

interface MapFieldConfig {
  key: ProductInputField;
  label: string;
  autoIndexKeys?: boolean;
}

const identityFields: TextFieldConfig[] = [
  { key: 'productName', label: 'Product Name' },
  { key: 'brand', label: 'Brand' },
  { key: 'category', label: 'Category' },
  { key: 'subcategory', label: 'Subcategory' },
  { key: 'productType', label: 'Product Type' },
  { key: 'marketplace', label: 'Marketplace' },
];

const contentBriefFields: TextFieldConfig[] = [
  { key: 'brandWebsite', label: 'Brand Website' },
  { key: 'productContents', label: 'Product Contents', multiline: true },
  { key: 'uniqueSellingProposition', label: 'Unique Selling Proposition', multiline: true },
  { key: 'competitor', label: 'Competitor' },
  { key: 'targetAudience', label: 'Target Audience', multiline: true },
  { key: 'language', label: 'Language' },
  { key: 'brandDescription', label: 'Brand Description', multiline: true },
  { key: 'compliance', label: 'Compliance', multiline: true },
];

const mapFields: MapFieldConfig[] = [
  { key: 'variation', label: 'Variation' },
  { key: 'keyBenefit', label: 'Key Benefit', autoIndexKeys: true },
  { key: 'technicalSpec', label: 'Technical Spec' },
];

const keywordGroups: Array<{ title: string; fields: ListFieldConfig[] }> = [
  {
    title: 'Global Keywords',
    fields: [
      { key: 'globalMustHaveKeywords', label: 'Must-Have Keywords', tone: 'global' },
      { key: 'negativeKeywords', label: 'Negative Keywords', tone: 'negative' },
    ],
  },
  {
    title: 'Title Keywords',
    fields: [
      { key: 'titleHighPriorityKeywords', label: 'High Priority', tone: 'high' },
      { key: 'titleOptionalKeywords', label: 'Optional', tone: 'optional' },
    ],
  },
  {
    title: 'Bullet Point Keywords',
    fields: [
      { key: 'bulletPointHighPriorityKeywords', label: 'High Priority', tone: 'high' },
      { key: 'bulletPointOptionalKeywords', label: 'Optional', tone: 'optional' },
    ],
  },
  {
    title: 'Description Keywords',
    fields: [
      { key: 'descriptionHighPriorityKeywords', label: 'High Priority', tone: 'high' },
      { key: 'descriptionOptionalKeywords', label: 'Optional', tone: 'optional' },
    ],
  },
];

const assertProductInputFieldCoverage = (_coverage: { [key in ProductInputField]: true }) => undefined;

assertProductInputFieldCoverage({
  productName: true,
  brand: true,
  category: true,
  subcategory: true,
  productType: true,
  marketplace: true,
  brandWebsite: true,
  productContents: true,
  uniqueSellingProposition: true,
  compliance: true,
  competitor: true,
  targetAudience: true,
  language: true,
  brandDescription: true,
  variation: true,
  keyBenefit: true,
  technicalSpec: true,
  globalMustHaveKeywords: true,
  titleHighPriorityKeywords: true,
  titleOptionalKeywords: true,
  bulletPointHighPriorityKeywords: true,
  bulletPointOptionalKeywords: true,
  descriptionHighPriorityKeywords: true,
  descriptionOptionalKeywords: true,
  negativeKeywords: true,
});

const renderListTags = (items?: string[], tone: ListFieldConfig['tone'] = 'optional') => (
  items && items.length > 0 ? items.map((item, idx) => (
    <Tag className={`custom-product-keyword-tag custom-product-keyword-tag--${tone}`} key={idx}>{item}</Tag>
  )) : (
    <span>—</span>
  )
);

const renderMapContentOnly = (data?: Record<string, string>) => (
  data && Object.keys(data).length > 0 ? (
    <ul style={{ marginBottom: 0, paddingLeft: 18 }}>
      {Object.entries(data).map(([key, value]) => (
        <li key={key}><strong>{key}:</strong> {value}</li>
      ))}
    </ul>
  ) : (
    <span>—</span>
  )
);

const requiredKeywordResearchFields: Array<{ key: ProductInputField; label: string }> = [
  { key: 'category', label: 'Category' },
  { key: 'subcategory', label: 'Subcategory' },
  { key: 'productType', label: 'Product Type' },
];

const isBlankValue = (value: unknown) => (
  value === undefined
  || value === null
  || (typeof value === 'string' && value.trim() === '')
);

const InlineEditActions: React.FC<{
  saving: boolean;
  onSave: () => void;
  onDiscard: () => void;
}> = ({ saving, onSave, onDiscard }) => (
  <div className="custom-product-edit-actions">
    <Button type="text" onClick={onDiscard} disabled={saving}>
      Discard
    </Button>
    <Button type="primary" onClick={onSave} loading={saving}>
      Save Changes
    </Button>
  </div>
);

const ProductInputEditBlock: React.FC<{
  label: string;
  children: React.ReactNode;
  saving: boolean;
  onSave: () => void;
  onDiscard: () => void;
}> = ({ label, children, saving, onSave, onDiscard }) => (
  <div className="custom-product-input-card custom-product-input-card--editing">
    <div className="custom-product-input-label">{label}</div>
    <div className="custom-product-input-body">
      <span className="custom-product-comparison-label">Product Input</span>
      {children}
      <InlineEditActions saving={saving} onSave={onSave} onDiscard={onDiscard} />
    </div>
  </div>
);

const ProductInputReadBlock: React.FC<{
  label: string;
  children: React.ReactNode;
  onEdit: () => void;
}> = ({ label, children, onEdit }) => (
  <div
    className="custom-product-input-card"
    onClick={onEdit}
    title="Click to edit"
  >
    <div className="custom-product-input-label">{label}</div>
    <div className="custom-product-input-body">
      {children}
    </div>
  </div>
);

const EditableItem: React.FC<{
  label: string;
  active: boolean;
  span?: number;
  readOnly: React.ReactNode;
  renderEditor: () => React.ReactNode;
  saving: boolean;
  onEdit: () => void;
  onSave: () => void;
  onDiscard: () => void;
}> = ({ label, active, span, readOnly, renderEditor, saving, onEdit, onSave, onDiscard }) => (
  <div className={active || span === 2 ? 'custom-product-input-card--wide' : undefined}>
    {active ? (
      <ProductInputEditBlock
        label={label}
        saving={saving}
        onSave={onSave}
        onDiscard={onDiscard}
      >
        {renderEditor()}
      </ProductInputEditBlock>
    ) : (
      <ProductInputReadBlock label={label} onEdit={onEdit}>
        {readOnly}
      </ProductInputReadBlock>
    )}
  </div>
);

const ProductInputSection: React.FC<Props> = ({ product }) => {
  const dispatch = useAppDispatch();
  const selectedCompany = useAppSelector((state) => state.companies.selectedCompany);

  const [activeField, setActiveField] = useState<ProductInputField | null>(null);
  const [saving, setSaving] = useState<boolean>(false);
  const [inputFormValues, setInputFormValues] = useState<CustomProductInput | undefined>(product?.input);

  useEffect(() => {
    setInputFormValues(product?.input);
    setActiveField(null);
  }, [product?.input]);

  const discardInlineEdit = () => {
    setInputFormValues(product.input);
    setActiveField(null);
  };

  const saveInlineEdit = async () => {
    if (!selectedCompany || !inputFormValues) return;

    const missingFields = requiredKeywordResearchFields
      .filter((field) => field.key === activeField && isBlankValue(inputFormValues[field.key]))
      .map((field) => field.label);

    if (missingFields.length > 0) {
      notification.error({
        message: 'Missing keyword research taxonomy',
        description: `Please fill ${missingFields.join(', ')} before saving. This field is required for keyword research and assignment.`,
      });
      return;
    }

    if (
      (activeField === 'marketplace' || activeField === 'language')
      && !isSupportedKeywordResearchLocale(inputFormValues.marketplace, inputFormValues.language)
    ) {
      notification.error({
        message: 'Unsupported keyword research locale',
        description: 'Please select a supported language and marketplace combination.',
      });
      return;
    }

    try {
      setSaving(true);
      await dispatch(updateCustomProduct({
        id: product.id,
        companyId: selectedCompany.id,
        input: inputFormValues,
      })).unwrap();
      await dispatch(refetchCustomProduct({
        companyId: selectedCompany.id,
        customProductId: product.id,
      }));
      notification.success({ message: 'Product updated' });
      setActiveField(null);
    } catch (e) {
      notification.error({ message: 'Failed to update product' });
    } finally {
      setSaving(false);
    }
  };

  const updateInputField = <K extends ProductInputField>(field: K, value: CustomProductInput[K]) => {
    setInputFormValues((prev: CustomProductInput | undefined) => (
      prev ? ({ ...prev, [field]: value }) : prev
    ));
  };

  const updateMarketplaceField = (marketplace: string) => {
    const normalizedMarketplace = normalizeLocaleCode(marketplace);
    setInputFormValues((prev: CustomProductInput | undefined) => {
      if (!prev) return prev;
      const currentLanguage = normalizeLocaleCode(prev.language);
      const languageOptions = getLanguageOptionsForMarketplace(normalizedMarketplace);
      const nextLanguage = languageOptions.some((option) => option.value === currentLanguage)
        ? currentLanguage
        : languageOptions[0]?.value || currentLanguage;

      return {
        ...prev,
        marketplace: normalizedMarketplace,
        language: nextLanguage,
      };
    });
  };

  const updateLanguageField = (language: string) => {
    const normalizedLanguage = normalizeLocaleCode(language);
    setInputFormValues((prev: CustomProductInput | undefined) => {
      if (!prev) return prev;
      const currentMarketplace = normalizeLocaleCode(prev.marketplace);
      const nextMarketplace = isSupportedKeywordResearchLocale(currentMarketplace, normalizedLanguage)
        ? currentMarketplace
        : getPreferredMarketplaceForLanguage(normalizedLanguage) || currentMarketplace;

      return {
        ...prev,
        marketplace: nextMarketplace,
        language: normalizedLanguage,
      };
    });
  };

  const renderLocaleSelectEditor = (field: TextFieldConfig) => {
    const value = normalizeLocaleCode(inputFormValues?.[field.key] as string | undefined);
    const options = field.key === 'marketplace'
      ? keywordResearchMarketplaces
      : keywordResearchLanguages;

    return (
      <Select
        showSearch
        style={{ width: '100%' }}
        value={value || undefined}
        options={options}
        optionFilterProp="label"
        placeholder={`Select ${field.label.toLowerCase()}`}
        onChange={(selectedValue) => {
          if (field.key === 'marketplace') {
            updateMarketplaceField(selectedValue);
            return;
          }
          updateLanguageField(selectedValue);
        }}
        autoFocus
      />
    );
  };

  const renderTextEditor = (field: TextFieldConfig) => {
    if (field.key === 'marketplace' || field.key === 'language') {
      return renderLocaleSelectEditor(field);
    }

    const value = String(inputFormValues?.[field.key] || '');

    if (field.multiline) {
      return (
        <Input.TextArea
          value={value}
          autoSize={{ minRows: 3 }}
          onChange={(event) => updateInputField(field.key, event.target.value as never)}
          autoFocus
        />
      );
    }

    return (
      <Input
        value={value}
        onChange={(event) => updateInputField(field.key, event.target.value as never)}
        autoFocus
      />
    );
  };

  const renderListEditor = (field: ListFieldConfig) => (
    <Select
      mode="tags"
      style={{ width: '100%' }}
      value={(inputFormValues?.[field.key] as string[] | undefined) || []}
      onChange={(vals) => updateInputField(field.key, vals as never)}
      tokenSeparators={[',']}
      placeholder="Add keywords"
      autoFocus
    />
  );

  const renderMapEditor = (field: MapFieldConfig) => (
    <KeyValueListEditor
      value={inputFormValues?.[field.key] as Record<string, string> | undefined}
      autoIndexKeys={field.autoIndexKeys}
      onChange={(val) => updateInputField(field.key, val as never)}
    />
  );

  const renderTextField = (field: TextFieldConfig) => (
    <EditableItem
      key={field.key}
      label={field.label}
      active={activeField === field.key}
      readOnly={product.input[field.key] ? (
        <Typography.Text>{String(product.input[field.key])}</Typography.Text>
      ) : '—'}
      saving={saving}
      onEdit={() => setActiveField(field.key)}
      onSave={() => {
        saveInlineEdit();
      }}
      onDiscard={discardInlineEdit}
      renderEditor={() => renderTextEditor(field)}
    />
  );

  const renderMapField = (field: MapFieldConfig) => (
    <EditableItem
      key={field.key}
      label={field.label}
      span={2}
      active={activeField === field.key}
      readOnly={renderMapContentOnly(product.input[field.key] as Record<string, string> | undefined)}
      saving={saving}
      onEdit={() => setActiveField(field.key)}
      onSave={saveInlineEdit}
      onDiscard={discardInlineEdit}
      renderEditor={() => renderMapEditor(field)}
    />
  );

  const renderKeywordField = (field: ListFieldConfig) => (
    <EditableItem
      key={field.key}
      label={field.label}
      active={activeField === field.key}
      readOnly={renderListTags(product.input[field.key] as string[] | undefined, field.tone)}
      saving={saving}
      onEdit={() => setActiveField(field.key)}
      onSave={saveInlineEdit}
      onDiscard={discardInlineEdit}
      renderEditor={() => renderListEditor(field)}
    />
  );

  return (
    <div className="custom-product-detail-scroll">
      <div className="custom-product-input-layout">
        <section className="custom-product-input-section">
          <h3 className="custom-product-section-title">Core Identification</h3>
          <div className="custom-product-input-grid custom-product-input-grid--compact">
            {identityFields.map(renderTextField)}
          </div>
        </section>

        <section className="custom-product-input-section">
          <h3 className="custom-product-section-title">Content Brief</h3>
          <div className="custom-product-input-grid">
            {contentBriefFields.map(renderTextField)}
          </div>
        </section>

        <section className="custom-product-input-section">
          <h3 className="custom-product-section-title">Semantic SEO Keywords</h3>
          <div className="custom-product-keyword-panel">
            {keywordGroups.map((group) => (
              <div className="custom-product-keyword-group" key={group.title}>
                <div className="custom-product-keyword-group-title">{group.title}</div>
                <div className="custom-product-keyword-grid">
                  {group.fields.map(renderKeywordField)}
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="custom-product-input-section">
          <h3 className="custom-product-section-title">Product Details & Claims</h3>
          <div className="custom-product-input-grid">
            {mapFields.map(renderMapField)}
          </div>
        </section>
      </div>
    </div>
  );
};

export { ProductInputSection };
